import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_STATUS_REVISAO,
  type CanalLembrete,
  type StatusRevisao,
} from '@reducee/contracts';

import { chamarApi } from '../api/cliente.ts';
import { RecusaDaApi } from '../componentes/RecusaDaApi.tsx';
import { CabecalhoDePagina, Botao, Cartao, Celula, Indicador, Linha, Pilula, Tabela, data, } from '../componentes/Superficie.tsx';

type RevisaoPendente = {
  id: string;
  contato: { id: string; nome: string };
  produto: string;
  operadora: string | null;
  statusRevisao: StatusRevisao;
  dataRevisao: string | null;
  dataRenovacao: string | null;
  responsavel: { id: string; nome: string };
  canais: { email: string | null; whatsapp: string | null };
  mensagem: string;
  ultimoLembreteEm: string | null;
};

/**
 * Lembretes da revisão do 9º mês.
 *
 * A aplicação não envia nada por conta própria: ela abre o e-mail ou o
 * WhatsApp de quem opera, com o texto pronto, e registra que o aviso saiu. É
 * esse registro — e não o envio — que tira o item da fila; sem ele, o mesmo
 * lembrete reapareceria todo dia.
 */
export function PaginaNotificacoes(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();

  const consulta = useQuery({
    queryKey: ['revisoes-pendentes'],
    queryFn: () => chamarApi<RevisaoPendente[]>('/notificacoes/revisoes'),
  });

  const registrar = useMutation({
    mutationFn: ({ id, canal }: { id: string; canal: CanalLembrete }) =>
      chamarApi<RevisaoPendente[]>(`/renovacoes/${id}/lembretes`, {
        metodo: 'POST',
        corpo: { canal },
      }),
    onSuccess: (pendentes) => {
      clienteDeConsultas.setQueryData(['revisoes-pendentes'], pendentes);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['renovacoes'] });
    },
  });

  const pendentes = consulta.data ?? [];
  const hoje = new Date();
  const atrasadas = pendentes.filter(
    (linha) => linha.dataRevisao && new Date(linha.dataRevisao) < hoje && !linha.ultimoLembreteEm,
  ).length;

  function avisar(revisao: RevisaoPendente, canal: 'EMAIL' | 'WHATSAPP'): void {
    const destino = canal === 'EMAIL' ? revisao.canais.email : revisao.canais.whatsapp;
    if (!destino) return;

    if (canal === 'EMAIL') {
      const assunto = encodeURIComponent(`Reducee · Revisão preventiva — ${revisao.contato.nome}`);
      window.location.href = `mailto:${destino}?subject=${assunto}&body=${encodeURIComponent(revisao.mensagem)}`;
    } else {
      const numero = destino.replace(/\D/g, '');
      window.open(
        `https://wa.me/${numero}?text=${encodeURIComponent(revisao.mensagem)}`,
        '_blank',
        'noopener',
      );
    }

    registrar.mutate({ id: revisao.id, canal });
  }

  return (
    <section>
      <CabecalhoDePagina
        area="Relacionamento"
        titulo="Notificações"
        descricao="Lembretes da revisão preventiva do 9º mês para a responsável pelo cliente."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Indicador
          rotulo="Pendentes"
          valor={String(pendentes.filter((linha) => !linha.ultimoLembreteEm).length)}
          severidade="alerta"
        />
        <Indicador rotulo="Vencidas" valor={String(atrasadas)} severidade="erro" />
        <Indicador
          rotulo="Notificadas"
          valor={String(pendentes.filter((linha) => linha.ultimoLembreteEm).length)}
          severidade="sucesso"
        />
        <Indicador
          rotulo="Em análise"
          valor={String(pendentes.filter((linha) => linha.statusRevisao === 'EM_ANALISE').length)}
        />
        <Indicador
          rotulo="Oportunidades"
          valor={String(
            pendentes.filter((linha) => linha.statusRevisao === 'OPORTUNIDADE_ENCONTRADA').length,
          )}
        />
      </div>

      <RecusaDaApi erro={registrar.error} padrao="Não foi possível registrar o lembrete." />

      <Cartao
        className="mt-6"
        titulo="Fila de lembretes"
        descricao="O sistema gera o lembrete quando o contrato atinge o 9º mês de vigência."
      >
        <Tabela
          colunas={[
            'Revisão',
            'Cliente',
            'Responsável',
            'Produto',
            'Status análise',
            'E-mail',
            'WhatsApp',
            'Notificação',
          ]}
          vazio="Nenhum lembrete do 9º mês pendente hoje."
          carregando={consulta.isLoading}
        >
          {pendentes.map((revisao) => (
            <Linha key={revisao.id}>
              <Celula>{data(revisao.dataRevisao)}</Celula>
              <Celula className="font-medium">{revisao.contato.nome}</Celula>
              <Celula>{revisao.responsavel.nome}</Celula>
              <Celula>
                {revisao.produto}
                {revisao.operadora ? ` · ${revisao.operadora}` : ''}
              </Celula>
              <Celula>
                <Pilula tom={revisao.statusRevisao === 'PENDENTE' ? 'alerta' : 'informacao'}>
                  {ROTULO_STATUS_REVISAO[revisao.statusRevisao]}
                </Pilula>
              </Celula>
              <Celula>
                {revisao.canais.email ? (
                  <Botao tamanho="mini" onClick={() => avisar(revisao, 'EMAIL')}>
                    Enviar e-mail
                  </Botao>
                ) : (
                  <span className="text-xs text-texto-suave">Cadastre em Configurações</span>
                )}
              </Celula>
              <Celula>
                {revisao.canais.whatsapp ? (
                  <Botao tamanho="mini" onClick={() => avisar(revisao, 'WHATSAPP')}>
                    Abrir WhatsApp
                  </Botao>
                ) : (
                  <span className="text-xs text-texto-suave">Cadastre em Configurações</span>
                )}
              </Celula>
              <Celula>
                {revisao.ultimoLembreteEm ? (
                  <Pilula tom="sucesso">Notificado {data(revisao.ultimoLembreteEm)}</Pilula>
                ) : (
                  <Botao
                    tamanho="mini"
                    onClick={() => registrar.mutate({ id: revisao.id, canal: 'MANUAL' })}
                    desabilitado={registrar.isPending}
                  >
                    Marcar notificado
                  </Botao>
                )}
              </Celula>
            </Linha>
          ))}
        </Tabela>
      </Cartao>
    </section>
  );
}
